import { pluralizeWord } from "./string"
import dayjs from 'dayjs'

export type RecurringConfigValidationErrors = {
    [key: string]: string
}

const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']

export function currentDayCode(date?: string) {
    if (date) {
        let selectedDate: Date
        if (date.includes('T')) {
            selectedDate = new Date(date)
        } else {
            const [year, month, day] = date.split('-').map(Number)
            // Create date at UTC noon to avoid timezone/DST boundary issues
            selectedDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
        }
        return date.includes('T')
            ? dayMap[selectedDate.getDay()]
            : dayMap[selectedDate.getUTCDay()]
    }
    return dayMap[dayjs().day()]
}

export const daysOfWeek = [
    { label: 'M', value: 'MO', fullName: 'Monday' },
    { label: 'T', value: 'TU', fullName: 'Tuesday' },
    { label: 'W', value: 'WE', fullName: 'Wednesday' },
    { label: 'T', value: 'TH', fullName: 'Thursday' },
    { label: 'F', value: 'FR', fullName: 'Friday' },
    { label: 'S', value: 'SA', fullName: 'Saturday' },
    { label: 'S', value: 'SU', fullName: 'Sunday' }
]

export function getFrequencySuffix(frequency: 'daily' | 'weekly' | 'monthly' | 'yearly', interval: number): string {
    var suffix = ''
    switch (frequency) {
        case 'daily':
            suffix = 'day'
            break
        case 'weekly':
            suffix = 'week'
            break
        case 'monthly':
            suffix = 'month'
            break
        case 'yearly':
            suffix = 'year'
            break
    }
    return ' ' + pluralizeWord(suffix, interval)
}

const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return num + "st";
    if (j === 2 && k !== 12) return num + "nd";
    if (j === 3 && k !== 13) return num + "rd";
    return num + "th";
};

export type MonthlyOption = {
    label: string;
    value: string;
    data: {
        pattern: string;
        day?: number;
        ordinal?: number;
        dayName?: string;
    };
};

export function getMonthlyOptions(date: string): MonthlyOption[] {
    if (!date) return [];

    // Parse date safely (local date)
    let selectedDate: Date;
    if (date.includes('T')) {
        selectedDate = new Date(date);
    } else {
        const [year, month, day] = date.split('-').map(Number);
        selectedDate = new Date(year, month - 1, day);
    }

    const dayOfMonth = selectedDate.getDate();
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ...
    const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
    const dayName = dayMap[dayOfWeek];
    const dayNameFull = daysOfWeek.find(d => d.value === dayName)?.fullName || '';

    // Determine which occurrence (1st, 2nd, etc.)
    const firstDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const firstWeekdayOfMonth = firstDayOfMonth.getDay();
    const daysDiff = dayOfWeek - firstWeekdayOfMonth;
    const ordinal = Math.ceil((dayOfMonth - daysDiff) / 7);

    // Check if this is the last occurrence of this weekday in the month
    const nextWeek = new Date(selectedDate);
    nextWeek.setDate(selectedDate.getDate() + 7);
    const isLast = nextWeek.getMonth() !== selectedDate.getMonth();

    const options = [
        {
            label: `Monthly on day ${dayOfMonth}`,
            value: 'day',
            data: { pattern: 'day', day: dayOfMonth }
        },
        {
            label: isLast
                ? `Monthly on the last ${dayNameFull}`
                : `Monthly on the ${getOrdinalSuffix(ordinal)} ${dayNameFull}`,
            value: 'weekday',
            data: {
                pattern: 'weekday',
                ordinal: isLast ? -1 : ordinal,
                dayName: dayName
            }
        }
    ];

    return options;
};

export type RecurrenceObject = {
    frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval?: number;
    endDate?: string | null;
    count?: number | null;
    by_day?: string[];
    monthlyPattern?: 'day' | 'weekday' | null;
    monthlyOption?: MonthlyOption | null;
};

export type SeriesObject = {
    frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval?: number;
    end_date?: string | null;
    count?: number | null;
    by_day?: string[] | null;
    by_month_day?: number[] | null;
    timezone?: string;
    rrule?: string | null;
};

function formatUntil(dateStr?: string | null): string | undefined {
    if (!dateStr) return undefined;
    // RFC5545 expects YYYYMMDDT000000Z format
    const date = new Date(dateStr);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}${m}${d}T000000Z`;
}

function buildRRule(base: any): string {
    const parts: string[] = [];

    if (base.frequency) parts.push(`FREQ=${base.frequency.toUpperCase()}`);
    if (base.interval && base.interval !== 1) parts.push(`INTERVAL=${base.interval}`);
    if (base.by_day && base.by_day.length > 0) parts.push(`BYDAY=${base.by_day.join(',')}`);
    if (base.by_month_day && base.by_month_day.length > 0)
        parts.push(`BYMONTHDAY=${base.by_month_day.join(',')}`);
    if (base.count) parts.push(`COUNT=${base.count}`);
    if (base.end_date) {
        const until = formatUntil(base.end_date);
        if (until) parts.push(`UNTIL=${until}`);
    }

    return parts.join(';');
}

export function mapRecurrenceToMutation(config: RecurrenceObject): SeriesObject {

    const base: SeriesObject = {
        frequency: config.frequency,
        interval: config.interval ?? 1,
        end_date: config.endDate ?? null,
        count: config.count ?? null,
        by_day: null as string[] | null,
        by_month_day: null as number[] | null,
        timezone: 'UTC',
        rrule: null as string | null,
    };

    switch (config.frequency) {
        case 'daily':
            // nothing else needed
            break;

        case 'weekly':
            base.by_day = config.by_day ?? null;
            break;

        case 'monthly':
            if (config.monthlyPattern === 'day') {
                const day = config.monthlyOption?.data?.day;
                if (day) base.by_month_day = [day];
            } else if (config.monthlyPattern === 'weekday') {
                const { ordinal, dayName } = config.monthlyOption?.data || {};
                if (dayName) base.by_day = [`${ordinal}${dayName}`];
            }
            break;

        case 'yearly':
            // yearly may optionally use by_month_day if you want to persist the exact date
            break;
    }

    base.rrule = buildRRule(base);

    return base;
}